import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/components/shared/Visual";
import {
  ShieldCheck, Star, Upload, FileText, Save, ArrowLeft,
  CheckCircle2, Circle, User, MapPin, Globe, Award,
} from "lucide-react";

/**
 * ADMIN — CPO Vetting Form (Amber → Green pathway)
 * 8 sections per V2 spec:
 *  1. Basic Profile
 *  2. Operational Strengths (star ratings)
 *  3. Qualification Uploads
 *  4. Provisional Capability Assessment
 *  5. External Reference Check
 *  6. Verification Pathway (Amber → Live Assessment → Green)
 *  7. Deployment Assessment
 *  8. Team Building Readiness
 */

const STRENGTHS = [
  { k: "protective_driving", label: "Protective Driving" },
  { k: "bodily_handling", label: "Bodily Handling" },
  { k: "surveillance_recce", label: "Surveillance / Recce" },
  { k: "advance_work", label: "Advance Work" },
  { k: "first_aid_trauma", label: "First Aid / Trauma" },
  { k: "incident_reporting", label: "Incident Reporting" },
  { k: "fire_safety", label: "Fire & Safety" },
  { k: "reports_docs", label: "Reports / Documentation" },
  { k: "radio_comms", label: "Radio Comms" },
  { k: "team_leadership", label: "Team Leadership" },
];

const QUALS = [
  { k: "psa_sia", label: "PSA / SIA Licence" },
  { k: "cpo_cert", label: "CPO Certificate" },
  { k: "medical", label: "Medical / First Aid Certs" },
  { k: "driving", label: "Driving Licence" },
  { k: "weapons", label: "Weapons / Tactical Certs" },
  { k: "insurance", label: "Insurance / Additional Docs" },
];

export default function AdminCpoVetting() {
  const { userId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/admin/cpo-vetting/${userId}`).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, [userId]);

  if (!data) return <div className="p-8 txt-lo text-center">Loading…</div>;

  const rec = data.record;
  const set = (path, value) => {
    const clone = JSON.parse(JSON.stringify(rec));
    const keys = path.split(".");
    let cur = clone;
    for (let i = 0; i < keys.length - 1; i++) {
      cur[keys[i]] = cur[keys[i]] || {};
      cur = cur[keys[i]];
    }
    cur[keys[keys.length - 1]] = value;
    setData({ ...data, record: clone });
  };

  const save = async (status) => {
    setBusy(true);
    try {
      const payload = { ...rec, status: status || rec.status };
      const r = await api.post(`/admin/cpo-vetting/${userId}`, payload);
      setData({ ...data, record: r.data });
      toast.success(status === "green" ? "Approved as Verified Capable" : "Draft saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const status = rec.status || "amber";

  return (
    <div className="space-y-6" data-testid="cpo-vetting-page">
      {/* Hero */}
      <div className="page-hero flex items-start justify-between gap-4 flex-wrap">
        <div className="relative z-10">
          <Link to="/admin/staff" className="text-xs text-brand font-outfit font-bold uppercase tracking-widest flex items-center gap-1 mb-3 hover:underline">
            <ArrowLeft className="w-3.5 h-3.5" /> Staff
          </Link>
          <div className="eyebrow eyebrow-brand mb-2">CPO Network</div>
          <h1 className="text-3xl font-outfit font-bold txt-hi">CPO Vetting Form</h1>
          <div className="text-sm txt-md font-manrope mt-1">Close-protection vetting &amp; capability assessment.</div>
        </div>
        <div className="relative z-10 flex items-center gap-2">
          <StatusPill kind={status === "green" ? "active" : "standby"}>
            {status === "green" ? "Verified · Green" : "Amber · Provisional"}
          </StatusPill>
          <button onClick={() => save()} disabled={busy} className="btn-ghost-dark"><Save className="w-4 h-4 mr-1" /> Save draft</button>
          <button onClick={() => save("green")} disabled={busy} className="btn-brand">Mark Verified</button>
        </div>
      </div>

      {/* 1. Basic Profile */}
      <Section num={1} title="Basic Profile">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <ProfCell icon={User} label="Full name" value={data.user.full_name || "—"} />
          <ProfCell icon={Award} label="SecuSource ID" value={data.user.secusource_id || "—"} mono />
          <ProfCell icon={MapPin} label="Base location" value={data.user.base_location || "—"} />
          <ProfCell icon={ShieldCheck} label="Availability" value={data.user.availability || "—"} />
          <ProfCell icon={Globe} label="Languages" value={(data.user.languages || []).join(", ") || "—"} />
        </div>
      </Section>

      {/* 2. Operational Strengths */}
      <Section num={2} title="Operational Strengths">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-2">
          {STRENGTHS.map((s) => (
            <StarRow key={s.k} label={s.label} value={rec.strengths?.[s.k] || 0} onChange={(v) => set(`strengths.${s.k}`, v)} testId={`strength-${s.k}`} />
          ))}
        </div>
      </Section>

      {/* 3. Qualification Uploads */}
      <Section num={3} title="Qualification Uploads">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {QUALS.map((q) => {
            const uploaded = !!rec.assessment?.[`qual_${q.k}`];
            return (
              <button
                key={q.k}
                onClick={() => set(`assessment.qual_${q.k}`, uploaded ? "" : `${q.k}_uploaded_${Date.now()}`)}
                className={`card-inner p-3 text-left flex items-center gap-3 hover:border-brand/40 ${uploaded ? "border-gold-500/40" : ""}`}
                data-testid={`qual-${q.k}`}
              >
                <div className={`w-8 h-8 rounded-md flex items-center justify-center ${uploaded ? "bg-gold-500/20 text-gold-300" : "bg-white/5 txt-lo"}`}>
                  {uploaded ? <CheckCircle2 className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-outfit font-semibold txt-hi text-sm truncate">{q.label}</div>
                  <div className="eyebrow mt-0.5">{uploaded ? "Uploaded" : "Tap to mark received"}</div>
                </div>
              </button>
            );
          })}
        </div>
      </Section>

      {/* 4. Provisional Capability + 6. Verification Pathway */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Section num={4} title="Provisional Capability">
          <div className="card-inner p-4 border-l-4 border-l-amber-400">
            <StatusPill kind="standby">Provisionally Capable · Amber</StatusPill>
            <div className="text-sm txt-md font-manrope mt-3">
              Based on submitted documents and initial reference checks. Pending live job assessment and verification.
            </div>
            <div className="eyebrow mt-3 text-amber-300">Reviewed by SecuSource Vetting Team</div>
          </div>
        </Section>

        <Section num={6} title="Verification Pathway">
          <div className="flex items-center gap-3">
            <Step n={1} label="Provisionally Capable" sub="Amber" tone="amber" done />
            <div className="flex-1 h-px bg-white/10" />
            <Step n={2} label="Live Job Assessment" sub="Pending" tone="brand" done={status === "green"} />
            <div className="flex-1 h-px bg-white/10" />
            <Step n={3} label="Verified Capable" sub="Green" tone="emerald" done={status === "green"} />
          </div>
          <div className="text-xs txt-lo font-manrope mt-4">
            Complete a live deployment assessment and deployment room review to reach Green status.
          </div>
        </Section>
      </div>

      {/* 5. External Reference Check */}
      <Section num={5} title="External Reference Check">
        <div className="grid gap-3 sm:grid-cols-2">
          <TxtField label="Reference from previous company" v={rec.reference?.company || ""} onChange={(v) => set("reference.company", v)} testId="ref-company" />
          <TxtField label="Job type" v={rec.reference?.job_type || ""} onChange={(v) => set("reference.job_type", v)} testId="ref-job-type" />
          <TxtField label="Driving duties" v={rec.reference?.driving || ""} onChange={(v) => set("reference.driving", v)} testId="ref-driving" />
          <TxtField label="Medical duties" v={rec.reference?.medical || ""} onChange={(v) => set("reference.medical", v)} testId="ref-medical" />
          <TxtField label="Fire &amp; safety" v={rec.reference?.fire_safety || ""} onChange={(v) => set("reference.fire_safety", v)} testId="ref-fire" />
        </div>
        <div className="grid gap-3 sm:grid-cols-2 mt-3">
          <StarRow label="Reliability" value={rec.reference?.reliability || 0} onChange={(v) => set("reference.reliability", v)} testId="ref-reliability" />
          <StarRow label="Professionalism" value={rec.reference?.professionalism || 0} onChange={(v) => set("reference.professionalism", v)} testId="ref-professionalism" />
        </div>
        <div className="mt-3">
          <label className="eyebrow block mb-1">How was the task fulfilled?</label>
          <textarea
            rows={2}
            value={rec.reference?.notes || ""}
            onChange={(e) => set("reference.notes", e.target.value)}
            placeholder="Consistently performed to a high standard. Proactive, reliable and professional."
            className="w-full rounded-md p-2.5 text-sm"
            data-testid="ref-notes"
          />
        </div>
      </Section>

      {/* 7. Deployment Assessment */}
      <Section num={7} title="Deployment Assessment">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <TxtField label="Assessor" v={rec.assessment?.assessor || ""} onChange={(v) => set("assessment.assessor", v)} />
          <TxtField label="Deployment Room reviewer" v={rec.assessment?.reviewer || ""} onChange={(v) => set("assessment.reviewer", v)} />
          <NumField label="Score / 100" v={rec.assessment?.score || 0} onChange={(v) => set("assessment.score", Number(v) || 0)} testId="assess-score" />
        </div>
        <div className="grid gap-3 sm:grid-cols-3 mt-3">
          <StarRow label="Plan quality" value={rec.assessment?.plan_quality || 0} onChange={(v) => set("assessment.plan_quality", v)} />
          <StarRow label="SOP understanding" value={rec.assessment?.sop_understanding || 0} onChange={(v) => set("assessment.sop_understanding", v)} />
          <StarRow label="Team adaptability" value={rec.assessment?.team_adaptability || 0} onChange={(v) => set("assessment.team_adaptability", v)} />
        </div>
        <div className="mt-3">
          <label className="eyebrow block mb-1">Notes</label>
          <textarea
            rows={2}
            value={rec.assessment?.notes || ""}
            onChange={(e) => set("assessment.notes", e.target.value)}
            placeholder="Well-prepared, understands plan structure and contingencies. Good communication and team fit for executive protection teams."
            className="w-full rounded-md p-2.5 text-sm"
          />
        </div>
        <div className="mt-3 flex items-center gap-3">
          <div className="eyebrow">Overall result</div>
          <StatusPill kind={rec.assessment?.result === "pass" ? "active" : "neutral"}>{rec.assessment?.result || "Pending"}</StatusPill>
          <button onClick={() => set("assessment.result", rec.assessment?.result === "pass" ? "pending" : "pass")} className="btn-ghost-dark text-xs">
            Toggle Pass
          </button>
        </div>
      </Section>

      {/* 8. Team Building Readiness */}
      <Section num={8} title="Team Building Readiness">
        <div className="grid gap-3 sm:grid-cols-2">
          <TxtField label="Preferred locations" v={rec.readiness?.locations || ""} onChange={(v) => set("readiness.locations", v)} placeholder="Dublin, Ireland" />
          <TxtField label="Current availability" v={rec.readiness?.availability || ""} onChange={(v) => set("readiness.availability", v)} placeholder="19 May – 12 Jun 2026" />
          <TxtField label="Specialties / skills" v={rec.readiness?.skills || ""} onChange={(v) => set("readiness.skills", v)} placeholder="Executive protection, Protective driving, Advance work" />
        </div>
        <div className={`mt-4 card-inner p-4 border-l-4 ${status === "green" ? "border-l-gold-400" : "border-l-white/10"}`}>
          <div className="flex items-center gap-3">
            <CheckCircle2 className={`w-6 h-6 ${status === "green" ? "text-gold-400" : "txt-lo"}`} />
            <div className="flex-1">
              <div className="font-outfit font-bold txt-hi text-sm">Ready for Team Placement</div>
              <div className="text-xs txt-lo font-manrope">Complete assessments and uploads to reach green status.</div>
            </div>
            <button onClick={() => save("green")} disabled={busy || status === "green"} className="btn-brand text-xs">
              {status === "green" ? "Verified" : "Add to Team Pool"}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 mt-3 text-xs font-manrope">
            {[
              ["Verified documents", true],
              ["Provisional capability (Amber)", true],
              ["Live assessment pending", status !== "green"],
              ["Green fit", status === "green"],
            ].map(([l, ok]) => (
              <div key={l} className="flex items-center gap-2">
                {ok ? <CheckCircle2 className="w-3.5 h-3.5 text-gold-400" /> : <Circle className="w-3.5 h-3.5 txt-lo" />}
                <span className={ok ? "txt-hi" : "txt-lo"}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>
    </div>
  );
}

function Section({ num, title, children }) {
  return (
    <div className="card-dark p-5">
      <div className="flex items-center gap-2 mb-4">
        <span className="w-6 h-6 rounded-full bg-brand/15 text-brand border border-brand/30 text-[11px] font-outfit font-bold flex items-center justify-center">{num}</span>
        <div className="eyebrow eyebrow-brand">{title}</div>
      </div>
      {children}
    </div>
  );
}

function ProfCell({ icon: Icon, label, value, mono }) {
  return (
    <div className="card-inner p-3 flex items-center gap-3">
      <Icon className="w-4 h-4 text-brand flex-shrink-0" />
      <div className="min-w-0">
        <div className="eyebrow">{label}</div>
        <div className={`text-sm txt-hi font-manrope truncate ${mono ? "font-mono text-xs" : "font-semibold"}`}>{value}</div>
      </div>
    </div>
  );
}

function StarRow({ label, value = 0, onChange, testId }) {
  return (
    <div className="card-inner p-3 flex items-center justify-between gap-3">
      <span className="text-sm txt-md font-manrope">{label}</span>
      <div className="flex gap-0.5" data-testid={testId}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} onClick={() => onChange(n)} className="hover:scale-110 transition-transform">
            <Star className={`w-4 h-4 ${n <= value ? "text-amber-400 fill-amber-400" : "txt-lo"}`} />
          </button>
        ))}
      </div>
    </div>
  );
}

function TxtField({ label, v, onChange, placeholder, testId }) {
  return (
    <div>
      <label className="eyebrow block mb-1">{label}</label>
      <input value={v} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full rounded-md p-2 text-sm" data-testid={testId} />
    </div>
  );
}

function NumField({ label, v, onChange, testId }) {
  return (
    <div>
      <label className="eyebrow block mb-1">{label}</label>
      <input type="number" value={v} onChange={(e) => onChange(e.target.value)} className="w-full rounded-md p-2 text-sm" data-testid={testId} />
    </div>
  );
}

function Step({ n, label, sub, tone, done }) {
  const bg = tone === "amber" ? "bg-amber-500/20 text-amber-300 border-amber-500/40" :
             tone === "emerald" ? "bg-gold-500/20 text-gold-300 border-gold-500/40" :
             "bg-brand/15 text-brand border-brand/30";
  return (
    <div className="text-center">
      <div className={`w-10 h-10 rounded-full border ${bg} flex items-center justify-center font-outfit font-bold ${done ? "" : "opacity-40"}`}>
        {done ? <CheckCircle2 className="w-5 h-5" /> : n}
      </div>
      <div className="text-xs font-outfit font-semibold txt-hi mt-2">{label}</div>
      <div className="eyebrow mt-0.5">{sub}</div>
    </div>
  );
}
